import { PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Checkbox, Flex, Form, InputNumber, Modal, Radio, Select, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type Warehouse } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { planAllows } from "@/lib/plan";
import { WarehouseFields, type WarehouseValues } from "./WarehouseFields";
import { RecordersDrawer } from "./Recorders";
import { PdasDrawer } from "./Pdas";

export function WarehouseSettings() {
  const { t } = useI18n();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<WarehouseValues>();
  const [editing, setEditing] = useState<Warehouse | "new" | null>(null);
  const [wmsOf, setWmsOf] = useState<Warehouse | null>(null);
  const [pdasOf, setPdasOf] = useState<Warehouse | null>(null);
  const navigate = useNavigate();

  const list = useQuery({ queryKey: ["warehouses"], queryFn: () => unwrap(api.GET("/v1/warehouses")) });

  const save = useMutation({
    mutationFn: (values: WarehouseValues) =>
      editing === "new"
        ? unwrap(api.POST("/v1/warehouses", { body: values }))
        : unwrap(
            api.PATCH("/v1/warehouses/{warehouseID}", {
              params: { path: { warehouseID: (editing as Warehouse).id } },
              body: { ...values, status: (editing as Warehouse).status },
            }),
          ),
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ["warehouses"] });
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });

  const setStatus = (w: Warehouse, status: "active" | "archived") =>
    unwrap(
      api.PATCH("/v1/warehouses/{warehouseID}", {
        params: { path: { warehouseID: w.id } },
        body: { name: w.name, address: w.address, city: w.city, postalCode: w.postalCode, status },
      }),
    )
      .then(() => queryClient.invalidateQueries({ queryKey: ["warehouses"] }))
      .catch((err: unknown) => void message.error(errorText(err)));

  const open = (target: Warehouse | "new") => {
    setEditing(target);
    form.resetFields();
    if (target !== "new") form.setFieldsValue(target);
  };

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("settings.warehouses.title")}
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => open("new")}>
          {t("settings.warehouses.add")}
        </Button>
      </Flex>

      <Table<Warehouse>
        rowKey="id"
        loading={list.isPending}
        dataSource={list.data?.items}
        pagination={false}
        locale={{ emptyText: t("settings.warehouses.empty") }}
        columns={[
          { title: t("settings.warehouses.code"), dataIndex: "code", width: 120, render: (v: string) => <span className="z-num">{v}</span> },
          {
            title: t("common.name"),
            dataIndex: "name",
            render: (v: string, w) => (
              <Space>
                {v}
                {w.isDefault && <Tag>{t("settings.warehouses.default")}</Tag>}
              </Space>
            ),
          },
          { title: t("settings.warehouses.city"), dataIndex: "city" },
          {
            title: t("settings.warehouses.wms"),
            dataIndex: "wmsEnabled",
            width: 170,
            render: (on: boolean) =>
              on ? (
                <Tag color="processing">{t("settings.warehouses.wmsOn")}</Tag>
              ) : (
                <Typography.Text type="secondary">{t("settings.warehouses.wmsOff")}</Typography.Text>
              ),
          },
          {
            title: t("common.status"),
            dataIndex: "status",
            width: 140,
            render: (s: Warehouse["status"]) =>
              s === "active" ? <Tag color="success">{t("settings.warehouses.active")}</Tag> : <Tag>{t("settings.warehouses.archived")}</Tag>,
          },
          {
            title: t("common.actions"),
            width: 360,
            render: (_, w) => (
              <Space wrap>
                <Button size="small" onClick={() => open(w)}>
                  {t("common.edit")}
                </Button>
                {w.status === "active" && (
                  <Button size="small" onClick={() => setWmsOf(w)}>
                    {t("settings.warehouses.wmsSet")}
                  </Button>
                )}
                {w.wmsEnabled && w.status === "active" && (
                  <Button size="small" onClick={() => setPdasOf(w)}>
                    {t("pda.manage")}
                  </Button>
                )}
                {w.wmsEnabled && (
                  <Button size="small" type="link" onClick={() => navigate(`/app/warehouse/locations?wh=${w.id}`)}>
                    {t("settings.warehouses.manageLocations")}
                  </Button>
                )}
                {w.status === "active" && !w.isDefault && (
                  <Button
                    size="small"
                    danger
                    onClick={() =>
                      modal.confirm({
                        title: t("settings.warehouses.archive"),
                        content: t("settings.warehouses.archiveConfirm", { name: w.name }),
                        okText: t("settings.warehouses.archive"),
                        okButtonProps: { danger: true },
                        cancelText: t("common.cancel"),
                        onOk: () => setStatus(w, "archived"),
                      })
                    }
                  >
                    {t("settings.warehouses.archive")}
                  </Button>
                )}
                {w.status === "archived" && (
                  <Button size="small" onClick={() => void setStatus(w, "active")}>
                    {t("settings.warehouses.restore")}
                  </Button>
                )}
              </Space>
            ),
          },
        ]}
      />

      <WmsModal warehouse={wmsOf} onClose={() => setWmsOf(null)} />
      <PdasDrawer warehouse={pdasOf} onClose={() => setPdasOf(null)} />
      <Modal
        open={editing !== null}
        title={editing === "new" ? t("settings.warehouses.add") : t("settings.warehouses.edit")}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        confirmLoading={save.isPending}
        onOk={() => form.submit()}
        onCancel={() => setEditing(null)}
        destroyOnHidden
        width={640}
      >
        <Form form={form} layout="vertical" requiredMark={false} onFinish={(v) => save.mutate(v)}>
          <WarehouseFields creating={editing === "new"} />
        </Form>
      </Modal>
    </>
  );
}

type WmsValues = {
  wmsEnabled: boolean;
  requireBoxScan: boolean;
  containerRequired: boolean;
  recordPacking: boolean;
  videoRetentionDays: number;
  pickByZone: boolean;
  recorder: "browser" | "agent";
  palletReceiving: boolean;
  palletMaxSkus: number;
  palletLabel: "100x150" | "100x100" | "100x50";
};

/** "Atur WMS": run WMS in a warehouse. How orders are picked is set per pool. */
function WmsModal({ warehouse, onClose }: { warehouse: Warehouse | null; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const zonePicking = planAllows(me?.workspace?.plan, "zone_picking");
  const agentAllowed = planAllows(me?.workspace?.plan, "recorder_agent");
  const [recorders, setRecorders] = useState<Warehouse | null>(null);
  const [form] = Form.useForm<WmsValues>();
  const on = Form.useWatch("wmsEnabled", form);
  const recordOn = Form.useWatch("recordPacking", form);
  const palletOn = Form.useWatch("palletReceiving", form);
  const save = useMutation({
    mutationFn: async (v: WmsValues) => {
      const w = warehouse!;
      if (
        v.requireBoxScan !== w.requireBoxScan ||
        v.containerRequired !== w.containerRequired ||
        v.recordPacking !== w.recordPacking ||
        v.videoRetentionDays !== w.videoRetentionDays ||
        v.pickByZone !== w.pickByZone ||
        v.recorder !== w.recorder ||
        v.palletReceiving !== w.palletReceiving ||
        v.palletMaxSkus !== w.palletMaxSkus ||
        v.palletLabel !== w.palletLabel
      ) {
        await unwrap(
          api.PATCH("/v1/warehouses/{warehouseID}", {
            params: { path: { warehouseID: w.id } },
            body: { name: w.name, address: w.address, city: w.city, postalCode: w.postalCode, status: w.status, requireBoxScan: v.requireBoxScan,
              containerRequired: v.containerRequired, recordPacking: v.recordPacking, videoRetentionDays: v.videoRetentionDays, pickByZone: v.pickByZone,
              recorder: v.recorder, palletReceiving: v.palletReceiving, palletMaxSkus: v.palletMaxSkus, palletLabel: v.palletLabel },
          }),
        );
      }
      if (v.wmsEnabled !== w.wmsEnabled) {
        await unwrap(api.PUT("/v1/warehouses/{warehouseID}/wms", { params: { path: { warehouseID: w.id } }, body: { wmsEnabled: v.wmsEnabled } }));
      }
    },
    onSuccess: () => {
      void message.success(t("settings.warehouses.wmsSaved"));
      for (const key of ["warehouses", "inventory", "locations"]) void queryClient.invalidateQueries({ queryKey: [key] });
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const changing = warehouse !== null && on !== undefined && on !== warehouse.wmsEnabled;
  return (
    <Modal
      open={warehouse !== null}
      title={warehouse ? t("settings.warehouses.wmsTitle", { name: warehouse.name }) : ""}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {warehouse && (
        <Form form={form} layout="vertical" initialValues={{ wmsEnabled: warehouse.wmsEnabled, requireBoxScan: warehouse.requireBoxScan, containerRequired: warehouse.containerRequired, recordPacking: warehouse.recordPacking, videoRetentionDays: warehouse.videoRetentionDays, pickByZone: warehouse.pickByZone, recorder: warehouse.recorder,
          palletReceiving: warehouse.palletReceiving, palletMaxSkus: warehouse.palletMaxSkus, palletLabel: warehouse.palletLabel }} onFinish={(v) => save.mutate(v)}>
          <Typography.Paragraph type="secondary">{t("settings.warehouses.wmsBody")}</Typography.Paragraph>
          <Form.Item name="wmsEnabled" valuePropName="checked">
            <Checkbox>{t("settings.warehouses.wmsEnable")}</Checkbox>
          </Form.Item>
          {changing && (
            <Alert
              type={on ? "info" : "warning"}
              showIcon
              style={{ marginBottom: 16 }}
              title={on ? t("settings.warehouses.wmsEnableNote") : t("settings.warehouses.wmsDisableNote")}
            />
          )}
          {on && (
            <Form.Item name="containerRequired" valuePropName="checked" extra={t("settings.warehouses.containerRequiredHelp")}>
              <Checkbox>{t("settings.warehouses.containerRequired")}</Checkbox>
            </Form.Item>
          )}
          {on && (
            <Form.Item name="palletReceiving" valuePropName="checked" extra={t("settings.warehouses.palletReceivingHelp")}>
              <Checkbox>{t("settings.warehouses.palletReceiving")}</Checkbox>
            </Form.Item>
          )}
          {on && palletOn && (
            <Flex gap={12} wrap>
              <Form.Item name="palletMaxSkus" label={t("settings.warehouses.palletMaxSkus")} style={{ width: 200 }}>
                <InputNumber min={1} max={50} precision={0} style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="palletLabel" label={t("settings.warehouses.palletLabel")} style={{ width: 200 }}>
                <Select options={["100x150", "100x100", "100x50"].map((s) => ({ value: s, label: `${s} mm` }))} />
              </Form.Item>
            </Flex>
          )}
          {on && (
            <Form.Item name="pickByZone" valuePropName="checked" extra={t(zonePicking ? "settings.warehouses.pickByZoneHelp" : "settings.warehouses.pickByZonePlan")}>
              <Checkbox disabled={!zonePicking && !warehouse.pickByZone}>{t("settings.warehouses.pickByZone")}</Checkbox>
            </Form.Item>
          )}
          {on && (
            <Form.Item name="requireBoxScan" valuePropName="checked" extra={t("settings.warehouses.requireBoxScanHelp")}>
              <Checkbox>{t("settings.warehouses.requireBoxScan")}</Checkbox>
            </Form.Item>
          )}
          {on && (
            <Form.Item name="recordPacking" valuePropName="checked" extra={t("settings.warehouses.recordPackingHelp")}>
              <Checkbox>{t("settings.warehouses.recordPacking")}</Checkbox>
            </Form.Item>
          )}
          {on && recordOn && (
            <Form.Item name="recorder" label={t("settings.warehouses.recorder")} extra={t(agentAllowed ? "settings.warehouses.recorderHelp" : "settings.warehouses.recorderPlan")}>
              <Radio.Group
                style={{ display: "flex", flexDirection: "column", gap: 4 }}
                options={[
                  { value: "browser", label: t("settings.warehouses.recorder.browser") },
                  { value: "agent", label: t("settings.warehouses.recorder.agent"), disabled: !agentAllowed && warehouse.recorder !== "agent" },
                ]}
              />
            </Form.Item>
          )}
          {on && recordOn && agentAllowed && (
            <Button style={{ marginBottom: 16 }} onClick={() => setRecorders(warehouse)}>
              {t("rec.manage")}
            </Button>
          )}
          {on && recordOn && (
            <Form.Item name="videoRetentionDays" label={t("settings.warehouses.videoRetention")} extra={t("settings.warehouses.videoRetentionHelp")}>
              <InputNumber min={7} max={365} precision={0} suffix={t("settings.warehouses.days")} style={{ width: 160 }} />
            </Form.Item>
          )}
        </Form>
      )}
      <RecordersDrawer warehouse={recorders} onClose={() => setRecorders(null)} />
    </Modal>
  );
}
